import { useCallback, useEffect, useState } from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useAppSelector } from '~/redux/hooks';
import { EntitlementFeatures } from '~/redux/api/routes/subscriptions/constants';
import { LocalStorageApi } from '~/utils/storage';
import { AI_COACH_BANNER_LAST_DISMISSED } from '~/utils/storage/contants';

const AI_COACH_BANNER_FLAG = 'web-ai-coach-banner';

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export type AiCoachBannerPage = 'home' | 'library' | 'guides';

const shouldShowBanner = (): boolean => {
  const lastDismissed = LocalStorageApi?.getItem<number>(
    AI_COACH_BANNER_LAST_DISMISSED
  );
  if (!lastDismissed) return true;
  return Date.now() - lastDismissed >= ONE_WEEK_MS;
};

export const useAiCoachBanner = (page: AiCoachBannerPage) => {
  const isFeatureEnabled = useFeatureIsOn(AI_COACH_BANNER_FLAG);
  const userEntitlements = useAppSelector((state) => state.entitlements.data);
  const isMaleCoach = userEntitlements.some(
    (e) => e.feature_id === EntitlementFeatures.ai_coach_male
  );
  const hasAiCoach =
    isMaleCoach ||
    userEntitlements.some((e) => e.feature_id === EntitlementFeatures.ai_coach);

  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (!isFeatureEnabled || !hasAiCoach) return;
    setShouldShow(shouldShowBanner());
  }, [isFeatureEnabled, hasAiCoach, page]);

  const dismiss = useCallback(() => {
    LocalStorageApi?.setItem(AI_COACH_BANNER_LAST_DISMISSED, Date.now());
    setShouldShow(false);
  }, []);

  return { shouldShow, dismiss, isMaleCoach };
};
